import { Component, EventEmitter, Inject, Injector, Input, OnDestroy, OnInit, Output, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';

import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionService, AuthService, cleanObject, CustomFormatter, ISitemap, ISitemapCommand, SITEMAP } from 'pbc.angular';

import { IPostDurchgangRequest, IPostDurchgangResponse } from 'fa-kt.types';
import { PostDurchgangCommandService } from '../service';

@Component({
  selector: 'fa-kt-post-durchgang',
  templateUrl: './post-durchgang.component.html',
  styleUrls: ['./post-durchgang.component.css'],
})
export class PostDurchgangCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IPostDurchgangRequest) {
    this.patch(value);
  }
  @Output() valueChanged: EventEmitter<IPostDurchgangRequest> = new EventEmitter<IPostDurchgangRequest>();
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostDurchgangResponse> = new EventEmitter<IPostDurchgangResponse>();

  get modalRef() {
    return this.injector.get(NzModalRef);
  }

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private injector: Injector,
    private viewContainerRef: ViewContainerRef,
    private modal: NzModalService,
    private message: NzMessageService,
    private actions: ActionService,
    public auth: AuthService,
    public postDurchgang: PostDurchgangCommandService /* << more    */ /*    more >> */,
  ) {
    this.command = sitemap.FORMULARE.Commands.POST_DURCHGANG;
    this.form = this.fb.group({
      durchgang: this.fb.group({
        id: [null, []],
        formular: [null, [Validators.required]],
        gutachten: [null, [Validators.required]],
        projekt: [null, [Validators.required]],
        besichtigung: [null, []],
      }),
    });
    /* << constructor    */
    /*    constructor >> */
    this.patch();
  }

  ngOnInit() {
    this.postDurchgang.prepare().catch((e) => console.error(e));
    this.subscriptions.push(
      this.finished.subscribe((result) => {
        if (this.modalRef) {
          this.modalRef.destroy(result);
        }
      }),
      this.valid.subscribe((valid) => {
        if (valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.form.valueChanges.subscribe((value: IPostDurchgangRequest) => {
        this.valueChanged.emit(value);
        this.valid.emit(this.form.valid);
      }),
      this.durchgangId?.valueChanges.subscribe(async (id) => {
        /* << on-durchgang-id-change    */
        /*    on-durchgang-id-change >> */
      }) as Subscription,
      this.durchgangFormular?.valueChanges.subscribe(async (formular) => {
        /* << on-durchgang-formular-change    */
        /*    on-durchgang-formular-change >> */
      }) as Subscription,
      this.durchgangGutachten?.valueChanges.subscribe(async (gutachten) => {
        /* << on-durchgang-gutachten-change    */
        /*    on-durchgang-gutachten-change >> */
      }) as Subscription,
      this.durchgangProjekt?.valueChanges.subscribe(async (projekt) => {
        /* << on-durchgang-projekt-change    */
        /*    on-durchgang-projekt-change >> */
      }) as Subscription,
      this.durchgangBesichtigung?.valueChanges.subscribe(async (besichtigung) => {
        /* << on-durchgang-besichtigung-change    */
        /*    on-durchgang-besichtigung-change >> */
      }) as Subscription,
      /* << subscibe    */
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    /* << end    */
    /*    end >> */
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IPostDurchgangRequest) {
    if (value) {
      value = cleanObject(value);
      if (value.durchgang) {
        value.durchgang = cleanObject(value.durchgang);
      }
      this.form.patchValue(value);
    }
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload.durchgang) {
        payload.durchgang = cleanObject(payload.durchgang);
      }
      const response: IPostDurchgangResponse = await this.postDurchgang.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls[validation.property].get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get durchgangId() {
    return this.form.controls.durchgang.get('id');
  }
  get durchgangFormular() {
    return this.form.controls.durchgang.get('formular');
  }
  get durchgangGutachten() {
    return this.form.controls.durchgang.get('gutachten');
  }
  get durchgangProjekt() {
    return this.form.controls.durchgang.get('projekt');
  }
  get durchgangBesichtigung() {
    return this.form.controls.durchgang.get('besichtigung');
  }
}
